import React, { useEffect, useState } from 'react';
import type {
  AuthContextValue,
  User,
  SubscriptionAssignment,
  SubscriptionAssignmentState,
} from '../auth/AuthContext';
import { useErrorNotification } from '../hooks/useErrorNotification';
import { Modal } from './Modal';
import { trackEvent } from '../analytics';

type StatusMessage = { type: 'ok' | 'err' | 'info'; text: string };

type BillingPlanPrice = {
  unitAmount: number | null;
  currency: string | null;
  interval: string | null;
  intervalCount: number | null;
  trialDays: number | null;
};

type BillingPlan = {
  code: string;
  name: string;
  description: string | null;
  isDefaultForNewUsers: boolean;
  price: BillingPlanPrice;
};

type AccountSettingsProps = {
  user: User | null;
  authedRequest: AuthContextValue['authedRequest'];
  authToken: string | null;
  loginSuccess: AuthContextValue['loginSuccess'];
};

const SUBSCRIPTION_STATUS_LABELS: Record<SubscriptionAssignmentState, string> = {
  active: 'Active',
  scheduled: 'Scheduled',
  expired: 'Expired',
  inactive: 'Inactive',
  plan_inactive: 'Plan inactive',
};

function formatDateTime(value: string | null): string {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

function formatAddedVia(value: string): string {
  return value
    .split('_')
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function describeInterval(interval: string | null, count: number | null): string | null {
  if (!interval) {
    return null;
  }

  if (!count || count === 1) {
    return interval;
  }

  return `${count} ${interval}${count > 1 ? 's' : ''}`;
}

function formatPlanPrice(plan: BillingPlan): string | null {
  const { unitAmount, currency, interval, intervalCount } = plan.price;
  if (unitAmount == null || !currency) {
    return null;
  }

  const amount = unitAmount / 100;
  let amountText: string;
  try {
    amountText = new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount);
  } catch {
    amountText = `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  }

  const intervalLabel = describeInterval(interval, intervalCount);
  return intervalLabel ? `${amountText} / ${intervalLabel}` : amountText;
}

function formatPlanTrial(plan: BillingPlan): string | null {
  const { trialDays } = plan.price;
  if (!trialDays || trialDays <= 0) {
    return null;
  }
  const suffix = trialDays === 1 ? '' : 's';
  return `${trialDays} day${suffix} free trial`;
}

export function AccountSettings({ user, authedRequest, authToken, loginSuccess }: AccountSettingsProps) {
  const [accountName, setAccountName] = useState<string>(user?.name ?? '');
  const [studentName, setStudentName] = useState<string>(user?.studentName ?? user?.name ?? '');
  const [status, setStatus] = useState<StatusMessage | null>(null);
  const [saving, setSaving] = useState(false);
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState<string | null>(null);
  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [selectedPlanCode, setSelectedPlanCode] = useState<string | null>(null);

  const subscriptionStatus = user?.subscriptionStatus ?? null;
  const assignments: SubscriptionAssignment[] = subscriptionStatus?.assignments ?? [];
  const contactEmail = subscriptionStatus?.contactEmail ?? null;
  const hasActiveSubscription = subscriptionStatus?.allowed ?? false;
  const isAdmin = user?.role === 'admin';

  useErrorNotification(status, { header: 'Account update error' });

  useEffect(() => {
    setAccountName(user?.name ?? '');
    setStudentName(user?.studentName ?? user?.name ?? '');
    setStatus(null);
  }, [user?.id, user?.name, user?.studentName]);

  useEffect(() => {
    const userId = user?.id;
    if (!userId) {
      setPlans([]);
      setSelectedPlanCode(null);
      setPlansError(null);
      setPlansLoading(false);
      return;
    }

    let cancelled = false;
    setPlansLoading(true);
    setPlansError(null);

    (async () => {
      try {
        const data = await authedRequest<{ plans: BillingPlan[] }>('/api/billing/plans');
        if (cancelled) {
          return;
        }
        setPlans(data.plans);
        setSelectedPlanCode((previous) => {
          if (previous && data.plans.some((plan) => plan.code === previous)) {
            return previous;
          }
          return data.plans[0]?.code ?? null;
        });
      } catch (err: any) {
        if (!cancelled) {
          setPlansError(err?.message || 'Failed to load subscription plans.');
          setPlans([]);
          setSelectedPlanCode(null);
        }
      } finally {
        if (!cancelled) {
          setPlansLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [authedRequest, user?.id]);

  if (!user) {
    return (
      <div className="card card-primary card-outline">
        <div className="card-header border-0">
          <h3 className="card-title mb-0">
            <i className="fas fa-user-cog me-2" aria-hidden="true" /> Account Settings
          </h3>
        </div>
        <div className="card-body">
          <p className="text-muted mb-0">Sign in to manage your account details.</p>
        </div>
      </div>
    );
  }

  const safeUser = user;

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    const trimmedAccountName = accountName.trim();
    const trimmedStudentName = studentName.trim();

    if (trimmedAccountName.length === 0) {
      setStatus({ type: 'err', text: 'Account user name cannot be empty.' });
      return;
    }
    if (trimmedStudentName.length === 0) {
      setStatus({ type: 'err', text: 'Student name cannot be empty.' });
      return;
    }

    const updates: Record<string, string> = {};
    if (trimmedAccountName !== (safeUser.name ?? '')) {
      updates.name = trimmedAccountName;
    }

    const defaultStudentName = safeUser.studentName ?? safeUser.name ?? '';
    if (trimmedStudentName !== defaultStudentName) {
      updates.studentName = trimmedStudentName;
    }

    if (Object.keys(updates).length === 0) {
      setStatus({ type: 'info', text: 'No changes to save.' });
      return;
    }

    setSaving(true);
    setStatus({ type: 'info', text: 'Saving account changes…' });
    try {
      const data = await authedRequest<{ user: User }>('/api/auth/me', {
        method: 'PATCH',
        body: JSON.stringify(updates),
      });
      if (!authToken) {
        throw new Error('Session expired. Please sign in again.');
      }
      loginSuccess(authToken, data.user);
      setStatus({ type: 'ok', text: 'Account updated.' });
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Unable to update account.' });
    } finally {
      setSaving(false);
    }
  }

  const handleStartCheckout = () => {
    trackEvent('subscription_start_clicked', {
      hasPlansLoaded: !plansLoading,
      availablePlanCount: plans.length,
      preselectedPlanCode: selectedPlanCode ?? plans[0]?.code ?? null,
    });
    setCheckoutError(null);
    if (plans.length === 0) {
      const message = plansError || 'No subscription plans are currently available.';
      setCheckoutError(message);
      setStatus({ type: 'err', text: message });
      return;
    }
    if (!selectedPlanCode) {
      setSelectedPlanCode(plans[0].code);
    }
    setPromoCode('');
    setCheckoutOpen(true);
  };

  const handleSubmitCheckout = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setCheckoutError(null);
    if (!selectedPlanCode) {
      setCheckoutError('Select a subscription plan to continue.');
      return;
    }
    setCheckoutLoading(true);
    setStatus({ type: 'info', text: 'Redirecting to Stripe checkout…' });
    try {
      const payload: { subscriptionCode: string; couponCode?: string } = {
        subscriptionCode: selectedPlanCode,
      };
      const trimmedPromo = promoCode.trim();
      if (trimmedPromo) {
        payload.couponCode = trimmedPromo;
      }
      const data = await authedRequest<{ checkoutUrl: string; sessionId: string }>('/api/billing/checkout', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const selectedPlan = plans.find((plan) => plan.code === selectedPlanCode) ?? null;
      trackEvent('subscription_checkout_redirected', {
        planCode: selectedPlanCode,
        planName: selectedPlan?.name ?? null,
        currency: selectedPlan?.price.currency ?? null,
        interval: selectedPlan?.price.interval ?? null,
        intervalCount: selectedPlan?.price.intervalCount ?? null,
        hasTrial: Boolean((selectedPlan?.price.trialDays ?? 0) > 0),
        usedPromoCode: Boolean(trimmedPromo),
      });
      window.location.href = data.checkoutUrl;
    } catch (err: any) {
      setCheckoutError(err?.message || 'Failed to start checkout.');
      setStatus({ type: 'err', text: err?.message || 'Failed to start checkout.' });
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleOpenBillingPortal = async () => {
    setPortalLoading(true);
    setStatus({ type: 'info', text: 'Opening Stripe billing portal…' });
    try {
      const data = await authedRequest<{ url: string }>('/api/billing/portal');
      window.location.href = data.url;
    } catch (err: any) {
      setStatus({ type: 'err', text: err?.message || 'Failed to open billing portal.' });
      setPortalLoading(false);
    }
  };

  return (
    <div className="card card-primary card-outline">
      <div className="card-header border-0">
        <h3 className="card-title mb-0">
          <i className="fas fa-user-cog me-2" aria-hidden="true" /> Account Settings
        </h3>
      </div>
      <div className="card-body">
        {subscriptionStatus && (
          <div
            className={`callout ${hasActiveSubscription ? 'callout-success' : 'callout-danger'}`}
            role="status"
          >
            <h4 className="fw-semibold mb-2">
              {hasActiveSubscription ? 'Subscription active' : 'Subscription required'}
            </h4>
            <p className="mb-2">
              {subscriptionStatus.summary}
              {!hasActiveSubscription &&
                subscriptionStatus.reason === 'missing' &&
                contactEmail && (
                  <>
                    {' '}
                    Please contact us at{' '}
                    <a href={`mailto:${contactEmail}`} className="fw-semibold">
                      {contactEmail}
                    </a>{' '}
                    for assistance.
                  </>
                )}
            </p>
            {subscriptionStatus.nextExpiration && hasActiveSubscription && (
              <p className="mb-0 text-muted">
                Valid until {formatDateTime(subscriptionStatus.nextExpiration)}
              </p>
            )}
            {!hasActiveSubscription && (
              <div className="mt-2">
                {plansLoading && <p className="mb-0 text-muted">Loading subscription plans…</p>}
                {!plansLoading && plansError && (
                  <p className="mb-0 text-danger">{plansError}</p>
                )}
                {!plansLoading && !plansError && plans.length > 0 && (
                  <p className="mb-0 text-muted">
                    {plans
                      .map((plan) => {
                        const price = formatPlanPrice(plan);
                        const trial = formatPlanTrial(plan);
                        const details = [price, trial].filter(Boolean).join(' • ');
                        return details ? `${plan.name} — ${details}` : plan.name;
                      })
                      .join(' • ')}
                  </p>
                )}
                {!plansLoading && !plansError && plans.length === 0 && (
                  <p className="mb-0 text-muted">No paid plans are currently available.</p>
                )}
              </div>
            )}
            {!isAdmin && (
              <div className="d-flex flex-wrap gap-2 mt-3">
                {!hasActiveSubscription && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleStartCheckout}
                    disabled={checkoutLoading || plansLoading || plans.length === 0}
                  >
                    {checkoutLoading ? 'Starting…' : 'Start subscription'}
                  </button>
                )}
                {hasActiveSubscription && (
                  <button
                    type="button"
                    className="btn btn-outline-primary"
                    onClick={handleOpenBillingPortal}
                    disabled={portalLoading}
                  >
                    {portalLoading ? 'Opening…' : 'Manage billing'}
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {subscriptionStatus && (
          <div className="mb-4">
            <h4 className="h5">Subscriptions</h4>
            {assignments.length === 0 ? (
              <div className="alert alert-light border">No subscriptions assigned yet.</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="table-light">
                    <tr>
                      <th scope="col">Plan</th>
                      <th scope="col">Status</th>
                      <th scope="col">Valid from</th>
                      <th scope="col">Valid until</th>
                      <th scope="col">Source</th>
                      <th scope="col">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {assignments.map((assignment) => {
                      const planStatus = assignment.subscriptionStatus === 'active' ? 'Active' : 'Inactive';
                      const manualNote = assignment.isManuallyActive ? null : 'Grant disabled';
                      const planInactiveNote = assignment.subscriptionStatus === 'inactive' ? 'Plan inactive' : null;
                      const notes = [assignment.addedReason, manualNote, planInactiveNote]
                        .filter((value): value is string => Boolean(value && value.trim().length > 0))
                        .join(' · ');
                      return (
                        <tr key={assignment.id}>
                          <td>
                            <div className="fw-semibold">{assignment.subscriptionName}</div>
                            <div className="text-uppercase text-muted small">{assignment.subscriptionCode}</div>
                          </td>
                          <td>
                            <span className={`badge bg-${badgeForStatus(assignment.status)} me-2`}>
                              {SUBSCRIPTION_STATUS_LABELS[assignment.status]}
                            </span>
                            <span className="text-muted small">{planStatus}</span>
                          </td>
                          <td>{formatDateTime(assignment.startAt)}</td>
                          <td>{assignment.endAt ? formatDateTime(assignment.endAt) : 'No expiry'}</td>
                          <td>{formatAddedVia(assignment.addedVia)}</td>
                          <td>{notes || '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {status && status.type !== 'err' && (
          <div
            className={`alert alert-${status.type === 'ok' ? 'success' : 'info'} alert-dismissible fade show`}
            role="status"
          >
            {status.text}
            <button type="button" className="btn-close" onClick={() => setStatus(null)} aria-label="Dismiss" />
          </div>
        )}

        <form className="row g-3" onSubmit={handleSubmit}>
          <div className="col-12 col-md-6">
            <label className="form-label fw-semibold">Account email</label>
            <div className="form-control-plaintext fw-semibold">{safeUser.email}</div>
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label fw-semibold" htmlFor="accountName">
              Account user name
            </label>
            <input
              id="accountName"
              type="text"
              className="form-control"
              value={accountName}
              onChange={(event) => setAccountName(event.target.value)}
              autoComplete="name"
              required
            />
          </div>
          <div className="col-12 col-md-6">
            <label className="form-label fw-semibold" htmlFor="studentName">
              Student name
            </label>
            <input
              id="studentName"
              type="text"
              className="form-control"
              value={studentName}
              onChange={(event) => setStudentName(event.target.value)}
              autoComplete="nickname"
              required
            />
          </div>
          <div className="col-12">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>

        {contactEmail && (
          <div className="alert alert-light border mt-4" role="note">
            Need help with your account or subscription? Contact us at{' '}
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
          </div>
        )}

        {checkoutOpen && (
          <Modal
            title="Start subscription"
            onClose={() => {
              if (!checkoutLoading) {
                setCheckoutOpen(false);
              }
            }}
            footer={
              <div className="d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setCheckoutOpen(false)}
                  disabled={checkoutLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  form="stripeCheckoutForm"
                  className="btn btn-primary"
                  disabled={checkoutLoading || plansLoading || plans.length === 0}
                >
                  {checkoutLoading ? 'Redirecting…' : 'Continue to Stripe'}
                </button>
              </div>
            }
          >
            <form id="stripeCheckoutForm" onSubmit={handleSubmitCheckout}>
              {plansLoading && (
                <div className="mb-3 text-muted">Loading subscription plans…</div>
              )}
              {!plansLoading && plans.length > 0 && (
                <div className="mb-3">
                  <span className="form-label fw-semibold">Choose a subscription</span>
                  <div className="d-flex flex-column gap-2 mt-2">
                    {plans.map((plan) => {
                      const price = formatPlanPrice(plan);
                      const trial = formatPlanTrial(plan);
                      const description = plan.description;
                      const radioId = `plan-${plan.code}`;
                      return (
                        <div className="form-check" key={plan.code}>
                          <input
                            className="form-check-input"
                            type="radio"
                            name="subscriptionPlan"
                            id={radioId}
                            value={plan.code}
                            checked={selectedPlanCode === plan.code}
                            onChange={() => setSelectedPlanCode(plan.code)}
                          />
                          <label className="form-check-label" htmlFor={radioId}>
                            <span className="fw-semibold">{plan.name}</span>
                            {price && <span className="ms-2 text-muted">{price}</span>}
                            {trial && <div className="small text-muted">{trial}</div>}
                            {description && <div className="small text-muted">{description}</div>}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              {!plansLoading && plansError && (
                <div className="alert alert-danger mb-3">{plansError}</div>
              )}
              {!plansLoading && !plansError && plans.length === 0 && (
                <div className="alert alert-light border mb-3">
                  No subscription plans are available right now. Please contact support if this persists.
                </div>
              )}
              <div className="mb-3">
                <label htmlFor="promoCode" className="form-label">
                  Promotion code (optional)
                </label>
                <input
                  id="promoCode"
                  type="text"
                  className="form-control"
                  value={promoCode}
                  onChange={(event) => setPromoCode(event.target.value)}
                  disabled={checkoutLoading}
                />
                <div className="form-text">
                  Enter a Stripe promotion code if you have one (for example a trial coupon).
                </div>
              </div>
              {checkoutError && <div className="alert alert-danger mb-0">{checkoutError}</div>}
            </form>
          </Modal>
        )}
      </div>
    </div>
  );
}

function badgeForStatus(status: SubscriptionAssignment['status']) {
  switch (status) {
    case 'active':
      return 'success';
    case 'scheduled':
      return 'info';
    case 'expired':
      return 'danger';
    case 'inactive':
    case 'plan_inactive':
      return 'secondary';
    default:
      return 'secondary';
  }
}
